/* eslint-disable sonarjs/cognitive-complexity */
import { nanoid } from 'nanoid';
import {
  PricingTableHelper,
  EditorHelper,
  CellType,
  NodeType,
  RowType,
  fallbackTextContent,
  ImageSize,
  Align,
  SignDateSettingsFormat,
  BorderStyle,
  fallbackParagraphContent,
  PlaceholderType,
} from '@getaccept/editor-lib';
import type {
  NodeUpdatePayload,
  Cell,
  EditorBlock,
  Row,
  CellPayload,
  RowPayload,
} from '@getaccept/editor-lib';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type {
  ContentPlaceholderPayload,
  ElementPlaceholderPayload,
} from '@getaccept/editor-lib/src/types/models';
import type { CreateNodePayload } from '../types/actions';

export class ContentActionHelper {
  static getBlockById(blocks: EditorBlock[], id: string): EditorBlock {
    return structuredClone(blocks.find(({ contentId }: EditorBlock) => contentId === id));
  }

  static getRowPayload(type: RowType, payload?: CreateNodePayload): RowPayload {
    if (type === RowType.SideBySide) {
      if (payload?.nodeType === NodeType.Link) {
        return this.getLinkRowPayload(payload);
      }
      return {
        rowId: nanoid(EditorHelper.NANO_ID_LENGTH),
        rowType: RowType.SideBySide,
        cells: [
          {
            cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
            width: 50,
            nodes: [
              {
                nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
                nodeType: NodeType.Image,
                locked: false,
              },
            ],
          },
          {
            cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
            width: 50,
            nodes: [
              {
                nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
                nodeType: NodeType.Text,
                locked: false,
                content: fallbackTextContent,
              },
            ],
          },
        ],
      };
    } else if (type === RowType.Columns) {
      return {
        columnCount: 3,
        rowId: nanoid(EditorHelper.NANO_ID_LENGTH),
        rowType: RowType.Columns,
        cells: Array.from({ length: 3 }).map(() => ({
          cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
          nodes: [
            {
              nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
              nodeType: NodeType.Image,
              locked: false,
            },
            {
              nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
              nodeType: NodeType.Text,
              locked: false,
              content: fallbackTextContent,
            },
          ],
          cellType: CellType.ImageAndText,
        })),
      };
    } else if (type === RowType.SlideShow) {
      return {
        rowId: nanoid(EditorHelper.NANO_ID_LENGTH),
        _tempSlideShowId: payload.slideshowPayload.id,
        _tempSlideShowName: payload.slideshowPayload.fileName,
        rowType: RowType.SlideShow,
        cells: [
          {
            cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
            width: 100,
            nodes: [
              {
                nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
                nodeType: NodeType.Image,
                locked: false,
              },
            ],
          },
        ],
      };
    } else {
      if (!payload) {
        return {
          rowId: nanoid(EditorHelper.NANO_ID_LENGTH),
          rowType: RowType.Standard,
          cells: [{ cellId: nanoid(EditorHelper.NANO_ID_LENGTH), width: 100, nodes: [] }],
        };
      }

      const { nodeType, ...rest }: CreateNodePayload = payload;
      let nodes = [
        { nodeId: nanoid(EditorHelper.NANO_ID_LENGTH), nodeType, locked: false, ...rest },
      ];
      if (nodeType === NodeType.PricingTable) {
        return this.getPricingTableRowPayload(payload);
      }
      if (nodeType === NodeType.Video) {
        nodes = this.getVideoNodePayload(payload);
      }
      if (nodeType === NodeType.Signature) {
        nodes = this.getSignatureNodePayload(payload);
      }
      if (nodeType === NodeType.ContractLink) {
        nodes = this.getContractLinkNodePayload();
      }
      if (nodeType === NodeType.ContentPlaceholder) {
        nodes = this.getContentPlaceholderNodePayload(payload);
      }
      if (nodeType === NodeType.PricingSummary) {
        nodes = this.getPricingSummaryNodePayload(payload);
      }
      return {
        rowId: nanoid(EditorHelper.NANO_ID_LENGTH),
        rowType: RowType.Standard,
        cells: [{ cellId: nanoid(EditorHelper.NANO_ID_LENGTH), width: 100, nodes }],
      };
    }
  }

  static getLinkRowPayload(payload: CreateNodePayload) {
    return {
      rowId: nanoid(EditorHelper.NANO_ID_LENGTH),
      rowType: RowType.SideBySide,
      cells: this.getLinkRowCellPayload(payload),
    };
  }

  static getLinkRowCellPayload(payload: NodeUpdatePayload) {
    return [
      {
        cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
        width: 30,
        nodes: [
          {
            nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
            nodeType: NodeType.Image,
            locked: false,
            srcUrl: payload.srcUrl,
            imageId: payload.imageId,
            attribution: payload.attribution,
          },
        ],
      },
      {
        cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
        width: 70,
        nodes: [
          {
            nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
            nodeType: NodeType.Text,
            locked: false,
            content: payload.description,
          },
          {
            nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
            nodeType: NodeType.Link,
            locked: false,
            content: payload.content,
            linkUrl: payload.linkUrl,
            align: payload?.align || Align.Left,
          },
        ],
      },
    ];
  }

  static getVideoNodePayload(payload: CreateNodePayload) {
    return [
      {
        nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
        nodeType: NodeType.Video,
        locked: false,
        srcUrl: payload.srcUrl,
        attribution: payload.attribution,
        url: payload.url,
        imageSize: payload.imageSize,
        videoId: payload.videoId,
        align: payload.align,
      },
    ];
  }

  static updateSlideShowPayload = (
    { id, type, width, nodes }: Cell,
    targetNodeId: string
  ): CellPayload => {
    const newNodes = nodes
      .filter(node => node.id !== targetNodeId)
      .map((node, index) => {
        const { id: nodeId, type: nodeType, ...rest } = node;
        return {
          ...rest,
          nodeId,
          nodeType,
          displayName: this.updateSlideNumber(node.displayName, index + 1),
        };
      });

    return { cellId: id, cellType: type, width, nodes: newNodes };
  };

  static updateSlideNumber(displayName: string, newSlideNumber: number) {
    const newDisplayName = displayName.split(' ');
    newDisplayName.pop();
    return `${newDisplayName.join(' ')} ${newSlideNumber}`;
  }

  static getImageNodePayload(payload: NodeUpdatePayload) {
    return [
      {
        cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
        width: 100,
        nodes: payload.images.map((image, index) => ({
          nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
          nodeType: NodeType.Image,
          locked: false,
          imageId: image.imageId,
          srcUrl: image.imageUrl,
          imageSize: ImageSize.Cover,
          displayName: `Slide ${index + 1}`,
        })),
      },
    ];
  }

  static getSignatureNodePayload(payload: NodeUpdatePayload) {
    return [
      {
        nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
        nodeType: NodeType.Signature,
        locked: false,
        align: Align.Left,
        fields: payload.fields,
        borderStyle: BorderStyle.Line,
        mergeTagsSettings: {
          fullName: true,
          title: false,
          companyName: true,
          companyNumber: false,
          email: false,
          signDate: {
            show: true,
            format: SignDateSettingsFormat.ISO,
          },
        },
      },
    ];
  }

  static getContractLinkNodePayload() {
    return [
      {
        nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
        nodeType: NodeType.ContractLink,
        contractId: null,
        locked: false,
        recipientMaps: [],
        name: null,
        fields: [],
        content: fallbackParagraphContent,
        contractDisplaySettings: {
          name: true,
          description: true,
          title: true,
          company: true,
          email: true,
        },
      },
    ];
  }

  static getPricingTableRowPayload(payload: CreateNodePayload) {
    const { currencySettings } = payload;
    const pricingTableNode = {
      nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
      nodeType: NodeType.PricingTable,
      preCalculated: false,
      locked: false,
      pricingTableSections: [PricingTableHelper.getNewPricingTableSection()],
      pricingTableSummaryValues: {
        displayName: t('pricing-summary'),
        discount: {
          value: '',
          enabled: false,
          flatFee: false,
          displayName: t('discount'),
        },
        tax: {
          value: '',
          enabled: false,
          flatFee: false,
          displayName: t('tax'),
        },
        price: {
          value: '',
          enabled: true,
          flatFee: false,
          displayName: t('Total'),
        },
      },
      currencySettings,
    };
    return {
      rowId: nanoid(),
      rowType: RowType.Standard,
      cells: [{ cellId: nanoid(), width: 100, nodes: [pricingTableNode] }],
    };
  }

  static getPricingSummaryNodePayload({
    pricingSummarySettings,
    labels,
    tables,
  }: CreateNodePayload) {
    return [
      {
        nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
        nodeType: NodeType.PricingSummary,
        locked: false,
        pricingSummarySettings,
        labels: labels || {
          nameColumn: t('name'),
          priceColumn: t('price'),
          totalPrice: t('total-price'),
        },
        tables,
      },
    ];
  }

  static getContentPlaceholderNodePayload({ placeholder }: CreateNodePayload) {
    let placeholderPayload: ContentPlaceholderPayload | ElementPlaceholderPayload = null;

    if (placeholder.type === PlaceholderType.Resource) {
      placeholderPayload = {
        type: PlaceholderType.Resource,
        resourceIds: [],
      };
    }

    if (placeholder.type === PlaceholderType.Element && placeholder?.elementType) {
      placeholderPayload = {
        type: PlaceholderType.Element,
        elementType: placeholder.elementType,
      };
    }

    return [
      {
        nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
        nodeType: NodeType.ContentPlaceholder,
        content: '',
        locked: false,
        placeholder: placeholderPayload,
      },
    ];
  }

  static cellToCellPayload = ({ id, type, width, nodes }: Cell, locked: boolean): CellPayload => {
    const newNodes = nodes.map(node => {
      const { id: nodeId, type: nodeType, ...rest } = node;
      return { ...rest, nodeId, nodeType, locked };
    });

    return { cellId: id, cellType: type, width, nodes: newNodes };
  };

  static getUpdatedCells = (
    cellCount: number,
    row: Row,
    locked: boolean,
    nodePayload: NodeUpdatePayload,
    targetNodeId: string
  ) => {
    const { cells } = row;

    if (cellCount > cells.length) {
      return [
        ...cells.map(cell => ContentActionHelper.cellToCellPayload(cell, locked)),
        ...Array.from({ length: Math.abs(cells.length - cellCount) }).map(() => ({
          cellId: nanoid(EditorHelper.NANO_ID_LENGTH),
          nodes: [
            {
              nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
              nodeType: NodeType.Image,
              locked,
            },
            {
              nodeId: nanoid(EditorHelper.NANO_ID_LENGTH),
              nodeType: NodeType.Text,
              locked,
              content: fallbackTextContent,
            },
          ],
          cellType: cells[0].type,
        })),
      ];
    } else if (cellCount < cells.length) {
      return cells
        .slice(0, cellCount)
        .map(cell => ContentActionHelper.cellToCellPayload(cell, locked));
    } else if (nodePayload) {
      return row.type === RowType.SlideShow
        ? ContentActionHelper.getImageNodePayload(nodePayload)
        : ContentActionHelper.getLinkRowCellPayload(nodePayload);
    } else if (row.type === RowType.SlideShow && targetNodeId) {
      return cells.map(cell => ContentActionHelper.updateSlideShowPayload(cell, targetNodeId));
    }

    return cells.map(cell => ContentActionHelper.cellToCellPayload(cell, locked));
  };
}
